import React from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Layout from './components/Layout';
import Home from './pages/Home';
import Leads from './pages/Leads';
import LandingPage from './pages/LandingPage';
import PoliticaPrivacidade from './pages/PoliticaPrivacidade';
import TermosUso from './pages/TermosUso';
import ChatbotPage from './pages/ChatbotPage';
import Login from './pages/Login';
import FilaAtendimentos from './pages/FilaAtendimentos';
import Usuarios from './pages/Usuarios';
import ChatbotPromptConfigs from './pages/ChatbotPromptConfigs';
import GoogleMCPDashboard from './pages/GoogleMCPDashboard';
import GoogleMCPCampaigns from './pages/GoogleMCPCampaigns';
import GoogleMCPLeads from './pages/GoogleMCPLeads';
import GoogleMCPAnalytics from './pages/GoogleMCPAnalytics';
import GoogleMCPAlerts from './pages/GoogleMCPAlerts';
import GoogleMCPSettings from './pages/GoogleMCPSettings';
import CampanhaManual from './pages/CampanhaManual';
import WhatsAppWeb from './pages/WhatsAppWeb';
import WhatsAppInbox from './pages/WhatsAppInbox';
import Organizations from './pages/Organizations';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ChatbotWidget from './components/ChatbotWidget';

// Paleta MedPrime alinhada à identidade visual oficial: azul-marinho
// profundo como cor dominante, com teal/ciano vibrante como acento
// (mesmas cores do logo). Sombras são tingidas com o tom primário em
// vez de preto puro (mais premium).
const theme = createTheme({
  palette: {
    primary: {
      main: '#123A61',
      light: '#3B6699',
      dark: '#0A2038',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#2DD4BF',
      light: '#7EEDDD',
      dark: '#0F9488',
      contrastText: '#0A2038',
    },
    background: {
      default: '#F7F9FB',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#101826',
      secondary: '#57646E',
    },
    divider: 'rgba(18, 58, 97, 0.12)',
  },
  shape: {
    borderRadius: 14,
  },
  typography: {
    fontFamily: '"Figtree", -apple-system, "Segoe UI", sans-serif',
    h1: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontWeight: 800,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontWeight: 700,
    },
    h5: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontWeight: 600,
    },
    h6: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontWeight: 600,
    },
    button: {
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 10,
        },
        sizeLarge: {
          paddingTop: 12,
          paddingBottom: 12,
          paddingLeft: 28,
          paddingRight: 28,
          fontSize: '1rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          boxShadow: '0 2px 12px rgba(14, 75, 79, 0.07)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 18,
        },
        elevation3: {
          boxShadow: '0 8px 32px rgba(14, 75, 79, 0.10)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          '&:before': { display: 'none' },
        },
      },
    },
  },
});

// Componente condicional para o ChatbotWidget
const ConditionalChatbotWidget = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  
  // Não mostrar o widget se o usuário estiver autenticado ou em rotas protegidas
  const isProtectedRoute = location.pathname.startsWith('/admin') || 
                          location.pathname.startsWith('/fila-atendimentos') ||
                          location.pathname === '/login';
  
  if (isAuthenticated || isProtectedRoute) {
    return null;
  }
  
  return <ChatbotWidget />;
};

// Bloqueia acesso às rotas internas (/admin, /fila-atendimentos) sem login.
// Aguarda a verificação inicial do token (loading) antes de decidir redirecionar,
// para não expulsar usuários já logados durante o refresh da página.
const ProtectedLayout = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Layout>
      <Outlet />
    </Layout>
  );
};

function App() {
  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/privacidade" element={<PoliticaPrivacidade />} />
            <Route path="/termos" element={<TermosUso />} />
            <Route path="/login" element={<Login />} />
            <Route path="/chatbot" element={<ChatbotPage />} />
            
            {/* Rotas protegidas com Layout */}
            <Route path="/admin" element={<ProtectedLayout />}>
              <Route index element={<Home />} />
              <Route path="leads" element={<Leads />} />
              <Route path="usuarios" element={<Usuarios />} />
              <Route path="chatbot-configs" element={<ChatbotPromptConfigs />} />
              <Route path="campanhas-manual" element={<CampanhaManual />} />
              <Route path="whatsapp-web" element={<WhatsAppWeb />} />
              <Route path="whatsapp-inbox" element={<WhatsAppInbox />} />
              <Route path="organizations" element={<Organizations />} />

              {/* Rotas do Google MCP */}
              <Route path="google-mcp" element={<GoogleMCPDashboard />} />
              <Route path="google-mcp/campaigns" element={<GoogleMCPCampaigns />} />
              <Route path="google-mcp/leads" element={<GoogleMCPLeads />} />
              <Route path="google-mcp/analytics" element={<GoogleMCPAnalytics />} />
              <Route path="google-mcp/alerts" element={<GoogleMCPAlerts />} />
              <Route path="google-mcp/settings" element={<GoogleMCPSettings />} />
            </Route>
            
            {/* Rota da Fila de Atendimentos (para consultores) */}
            <Route path="/fila-atendimentos" element={<ProtectedLayout />}>
              <Route index element={<FilaAtendimentos />} />
            </Route>
          </Routes>
          <ConditionalChatbotWidget />
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
